import { useEffect, useRef } from 'react';

interface GalleryItem {
  id: string;
  src: string;
  alt: string;
  label: string;
  sublabel: string;
  animation: 'parallax' | 'zoom' | 'pan' | 'pulse';
}

const galleryItems: GalleryItem[] = [
  {
    id: 'motorhome',
    src: 'https://public.readdy.ai/ai/img_res/c3ac01be728d4e69055cbc9b39942774.jpg',
    alt: 'Motorhome Racing',
    label: 'MOTORHOME RACING',
    sublabel: 'Noleggio Motorhome',
    animation: 'parallax',
  },
  {
    id: 'moto',
    src: 'https://readdy.ai/api/search-image?query=high%20performance%20racing%20motorcycle%20on%20track%2C%20motion%20blur%20effect%2C%20dramatic%20dark%20background%2C%20red%20and%20orange%20accent%20lights%2C%20professional%20motorsport%20photography%2C%20speed%20and%20power%2C%20cinematic%20dark%20atmosphere%2C%20ultra%20realistic%20detail&width=600&height=400&seq=gal002&orientation=landscape',
    alt: 'Moto da Competizione',
    label: 'MOTO DA COMPETIZIONE',
    sublabel: 'Noleggio Moto Racing',
    animation: 'zoom',
  },
  {
    id: 'stand',
    src: 'https://readdy.ai/api/search-image?query=modern%20professional%20exhibition%20stand%20at%20trade%20fair%2C%20sleek%20minimalist%20design%2C%20dramatic%20lighting%2C%20dark%20background%2C%20high%20end%20corporate%20event%20setup%2C%20LED%20displays%2C%20premium%20materials%2C%20cinematic%20atmosphere%2C%20ultra%20realistic&width=600&height=400&seq=gal003&orientation=landscape',
    alt: 'Stand Fieristico',
    label: 'STAND FIERISTICI',
    sublabel: 'Allestimenti Professionali',
    animation: 'pan',
  },
  {
    id: 'generatore',
    src: 'https://readdy.ai/api/search-image?query=industrial%20professional%20power%20generator%20equipment%2C%20dark%20dramatic%20background%2C%20orange%20and%20yellow%20accent%20lighting%2C%20high%20tech%20machinery%2C%20energy%20and%20power%20concept%2C%20cinematic%20photography%2C%20ultra%20realistic%20detail%2C%20professional%20industrial%20setting&width=600&height=400&seq=gal004&orientation=landscape',
    alt: 'Generatori',
    label: 'GENERATORI',
    sublabel: 'Energia per Eventi',
    animation: 'pulse',
  },
];

const AnimatedImage = ({ item }: { item: GalleryItem }) => {
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (item.animation !== 'parallax') return;
    const handleScroll = () => {
      if (!imgRef.current) return;
      const rect = imgRef.current.getBoundingClientRect();
      const scrolled = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
      const offset = scrolled * 60 - 30;
      const img = imgRef.current.querySelector('img');
      if (img) img.style.transform = `translateY(${offset}px) scale(1.15)`;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [item.animation]);

  const animationClass = {
    zoom: 'animate-slow-zoom',
    pan: 'animate-slow-pan',
    pulse: '',
    parallax: '',
  }[item.animation];

  return (
    <div
      ref={imgRef}
      className="relative overflow-hidden rounded-xl group cursor-pointer"
    >
      {/* Pulse border for generators */}
      {item.animation === 'pulse' && (
        <div className="absolute inset-0 rounded-xl border-2 border-orange-500/50 animate-pulse z-10 pointer-events-none" />
      )}

      <img
        src={item.src}
        alt={item.alt}
        className={`w-full h-full object-cover object-top transition-all duration-700 group-hover:brightness-125 ${animationClass}`}
      />

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent transition-all duration-500 group-hover:from-black/50" />

      {/* Red border glow on hover */}
      <div className="absolute inset-0 rounded-xl border border-red-600/0 group-hover:border-red-600/60 transition-all duration-500" />

      {/* Label */}
      <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
        <span className="font-['Exo_2'] font-black text-white text-sm md:text-base tracking-widest uppercase block">
          {item.label}
        </span>
        <span className="font-['Inter'] text-gray-400 text-xs tracking-wider mt-1 block">
          {item.sublabel}
        </span>
      </div>
    </div>
  );
};

const AnimatedGallery = () => (
  <section id="galleria" className="relative bg-[#08080c] py-24 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a10] to-[#08080c]" />

    <div className="relative z-10 max-w-7xl mx-auto px-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
        <div>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-orange-800/40 rounded-full mb-5 bg-orange-950/10">
            <span className="font-['Exo_2'] text-xs text-orange-400 tracking-[0.3em] uppercase">Le Nostre Attrezzature</span>
          </div>
          <h2 className="font-['Exo_2'] font-black text-white text-4xl md:text-5xl tracking-tight">
            FLOTTA <span className="text-orange-500">PREMIUM</span>
          </h2>
        </div>
        <p className="font-['Inter'] text-gray-500 text-sm max-w-xs leading-relaxed">
          Ogni attrezzatura è rigorosamente testata e pronta all&apos;uso per garantire le massime prestazioni.
        </p>
      </div>

      {/* Asymmetric Grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
        {/* Left - tall */}
        <div className="md:col-span-4 h-[400px] md:h-[700px]">
          <AnimatedImage item={galleryItems[0]} />
        </div>

        {/* Center - two stacked */}
        <div className="md:col-span-5 flex flex-col gap-4 md:gap-5">
          <div className="h-[280px] md:h-[340px]">
            <AnimatedImage item={galleryItems[1]} />
          </div>
          <div className="h-[280px] md:h-[340px]">
            <AnimatedImage item={galleryItems[2]} />
          </div>
        </div>

        {/* Right - with quote */}
        <div className="md:col-span-3 flex flex-col gap-4 md:gap-5">
          <div className="h-[280px] md:h-[380px]">
            <AnimatedImage item={galleryItems[3]} />
          </div>
          {/* Quote card */}
          <div className="flex-1 min-h-[200px] rounded-xl border border-red-900/30 bg-red-950/10 p-6 flex flex-col justify-between">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-double-quotes-l text-red-500 text-3xl" />
            </div>
            <div>
              <p className="font-['Inter'] text-gray-300 text-sm leading-relaxed italic mb-4">
                &ldquo;Partner affidabile per le nostre competizioni europee. Qualità e puntualità impeccabili.&rdquo;
              </p>
              <div>
                <span className="font-['Exo_2'] font-bold text-white text-sm block">Marco Ferretti</span>
                <span className="font-['Inter'] text-gray-500 text-xs">Team Manager, Racing Italia</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <style>{`
      @keyframes slowZoom {
        0%, 100% { transform: scale(1.0); }
        50% { transform: scale(1.06); }
      }
      @keyframes slowPan {
        0%, 100% { transform: translateX(0) scale(1.1); }
        50% { transform: translateX(-4%) scale(1.1); }
      }
      .animate-slow-zoom {
        animation: slowZoom 9s ease-in-out infinite;
      }
      .animate-slow-pan {
        animation: slowPan 11s ease-in-out infinite;
      }
    `}</style>
  </section>
);

export default AnimatedGallery;
